import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Table from 'components/Table/Table';
import moment from 'moment';
import { formatAddress } from 'utilities/format';
import Toolbar from '@material-ui/core/Toolbar';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import UpdateOrderDeliveryStaffButton from 'components/Order/UpdateOrderDeliveryStaffButton';
import DeleteOrderButton from './DeleteOrderButton';
import UpdateAddressButton from './UpdateAddressButton';
import CheckIcon from '@material-ui/icons/Check';
import CancelIcon from '@material-ui/icons/Cancel';

export default function TargetOrderTable({ data: inData, onUpdate, deliveryStaffOptions, restaurantOptions, onRowSelectionChange, ...props }) {
  const ordersSelected = useRef([]);
  const [data, setData] = useState();

  useEffect(() => {
    const newData = JSON.parse(JSON.stringify(inData));
    setData(newData);
  }, [inData]);

  if (!data) {
    return <></>;
  }

  const columns = [
    {
      name: 'referenceId',
      label: '編號',
      options: {
        display: true,
        filter: false,
        sort: true,
        customBodyRender(value) {
          return (value || '').split('-')[1] || value || '';
        },
      },
    },
    {
      name: 'elder.name',
      label: '送餐對象',
      options: {
        display: true,
        filter: false,
        sort: true,
        sortCompare(order) {
          return (obj1, obj2) => {
            if (obj1.data === obj2.data) {
              return (obj1.rowData[0] + obj1.rowData[1] > obj2.rowData[0] + obj2.rowData[1] ? 1 : -1) * (order === 'asc' ? 1 : -1);
            }
            return (obj1.data > obj2.data ? 1 : -1) * (order === 'asc' ? 1 : -1);
          };
        },
      },
    },
    {
      name: 'elder.address',
      label: '送餐地址',
      options: {
        display: true,
        filter: false,
        sort: true,
        customBodyRender(value) {
          return (
            <Grid container direction='row'>
              {(value?.lat && value?.lng) ? <Grid item><CheckIcon color="primary" /></Grid> : null}
              {(value?.lat === 0 && value?.lng === 0) ? <Grid item><CancelIcon color="secondary" /></Grid> : null}
              <Grid item xs={10}>
                <Typography variant='body2'>{formatAddress(value)}</Typography>
              </Grid>
            </Grid>
          );
        },
        sortCompare(order) {
          return (obj1, obj2) => {
            const addr1 = formatAddress(obj1.data);
            const addr2 = formatAddress(obj2.data);
            if (addr1 === addr2) {
              return (obj1.rowData[0] + obj1.rowData[1] > obj2.rowData[0] + obj2.rowData[1] ? 1 : -1) * (order === 'asc' ? 1 : -1);
            }
            return (addr1 > addr2 ? 1 : -1) * (order === 'asc' ? 1 : -1);
          };
        },
      },
    },
    {
      name: 'deliveryStaffId',
      label: '送餐大使',
      options: {
        display: true,
        filter: true,
        sort: true,
        customBodyRender(value) {
          const deliveryStaff = deliveryStaffOptions.find(({ id }) => id === value);
          if (deliveryStaff) {
            return deliveryStaff.name;
          }
          return '無';
        },
      },
    },
    {
      name: 'deliveryStaffFee',
      label: '送餐費用',
      options: {
        display: true,
        filter: true,
        sort: true,
        customBodyRender(value) {
          return value ? value : '無';
        },
      },
    },
    {
      name: 'deliveryDatetimes',
      label: '送餐時間',
      options: {
        display: true,
        filter: true,
        sort: true,
        customBodyRender(value) {
          if (value.length === 0) {
            return '無';
          }
          return moment(value[0]).format('LLLL');
        },
        sortCompare(order) {
          return (obj1, obj2) => {
            const val1 = moment(obj1.data[0]);
            const val2 = moment(obj2.data[0]);
            const diff = val1.diff(val2);
            if (diff === 0) {
              return (obj1.rowData[0] + obj1.rowData[1] > obj2.rowData[0] + obj2.rowData[1] ? 1 : -1) * (order === 'asc' ? 1 : -1);
            }
            return diff * (order === 'asc' ? 1 : -1);
          };
        },
      },
    },
    {
      name: 'restaurantId',
      label: '餐廳',
      options: {
        display: true,
        filter: true,
        sort: true,
        customBodyRender(value) {
          const restaurant = restaurantOptions.find(({ id }) => id === value);
          return restaurant ? restaurant.name : '無';
        },
      },
    },
    {
      name: 'mealItems',
      label: '餐點內容',
      options: {
        display: true,
        filter: true,
        sort: true,
        customBodyRender(value) {
          return value[0].name;
        },
        sortCompare(order) {
          return (obj1, obj2) => {
            if (obj1.data[0].name === obj2.data[0].name) {
              return (obj1.rowData[0] > obj2.rowData[0] ? 1 : -1) * (order === 'asc' ? 1 : -1);
            }
            return (obj1.data[0].name > obj2.data[0].name ? 1 : -1) * (order === 'asc' ? 1 : -1);
          };
        },
      },
    },
    {
      name: 'mealItems',
      label: '餐點數量',
      options: {
        display: true,
        filter: false,
        sort: true,
        customBodyRender(value) {
          return value[0].quantity;
        },
        sortCompare(order) {
          return (obj1, obj2) => {
            if (obj1.data[0].quantity === obj2.data[0].quantity) {
              return (obj1.rowData[0] + obj1.rowData[1] > obj2.rowData[0] + obj2.rowData[1] ? 1 : -1) * (order === 'asc' ? 1 : -1);
            }
            return (obj1.data[0].quantity - obj2.data[0].quantity) * (order === 'asc' ? 1 : -1);
          };
        },
      },
    },
    {
      name: 'noteForMeal',
      label: '餐點備註',
      options: {
        display: true,
        filter: false,
        sort: false,
      },
    },
    {
      name: 'noteForDelivery',
      label: '送餐備註',
      options: {
        display: true,
        filter: false,
        sort: false,
      },
    },
  ];

  const customToolbarSelect = ({ selectedRows, setSelectedRows }) => {
    const rowsSelected = selectedRows.data.map(({ dataIndex }) => dataIndex);
    return (
      <Toolbar>
        <UpdateOrderDeliveryStaffButton
          ordersSelected={ordersSelected}
          onUpdate={(deliveryStaffId) => {
            ordersSelected.current.forEach((order) => order.deliveryStaffId = deliveryStaffId);
            setSelectedRows([]);
            onUpdate && onUpdate(data);
          }}
          noSubmit={true}
        />
        {ordersSelected.current.every((order) => !!order.elder) &&
        <UpdateAddressButton
          ordersSelected={ordersSelected}
          onUpdate={(newAddress) => {
            ordersSelected.current.forEach((order) => order.elder.address = newAddress);
            setSelectedRows([]);
            onUpdate && onUpdate(data);
          }}
        />}
        <DeleteOrderButton
          rowsSelected={rowsSelected}
          onDelete={(rowsDeleted) => {
            const dataKeeped = data.filter((_d, index) => !rowsDeleted.includes(index));
            setSelectedRows([]);
            onUpdate && onUpdate(dataKeeped);
          }}
        />
      </Toolbar>
    );
  };

  return (
    <Table
      title={'匯入訂單'}
      isLoading={false}
      data={data}
      columns={columns}
      options={{
        selectableRows: 'multiple',
        isRowSelectable: () => true,
        rowsPerPage: 100,
      }}
      onRowSelectionChange={(_currentRowsSelected, _allRowsSelected, rowsSelected) => {
        ordersSelected.current = data.filter((_d, index) => rowsSelected.includes(index));
        onRowSelectionChange && onRowSelectionChange(rowsSelected);
      }}
      customToolbarSelect={customToolbarSelect}
      {...props}
    />
  );
}

TargetOrderTable.propTypes = {
  data: PropTypes.array,
  deliveryStaffOptions: PropTypes.array,
  restaurantOptions: PropTypes.array,
  onUpdate: PropTypes.func,
  onRowSelectionChange: PropTypes.func,
};
